/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	IconButton,
	Tooltip,
	TextField,
	Box,
	Typography,
} from '@mui/material';
import { Trash2, Pencil, Check, X, RefreshCcw } from 'lucide-react';
import { notificationServices } from '@/Services';
import gstCodeServices from '@/Services/gstCodeServices';
import { useAuth } from '@/Components/Auth/Login/auth';

// The console only lets a customer enter the GST Code and its Tax %. The
// GST split is fixed: IGST equals the tax %, while CGST and SGST are each
// half of it. We compute these for display only — the backend stores a
// single taxPercentage value.
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const isSuccess = (res) => res?.statusCode === 200 || res?.status === 200;

const DESCRIPTION_MAX_LENGTH = 30;

const emptyRow = () => ({
	id: null,
	gst_code: '',
	taxPercentage: '',
	description: '',
	isNew: true,
	isEditing: true,
});

const GstCodes = () => {
	const { userConfig = {} } = useAuth();
	const { level } = userConfig?.userDetails || {};
	const canEdit = level !== 1;

	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);

	const fetchList = async () => {
		setLoading(true);
		try {
			const response = await gstCodeServices.getGstCodes();
			if (isSuccess(response)) {
				const data = (response?.data ?? []).map((item) => ({
					...item,
					isNew: false,
					isEditing: false,
				}));
				setRows(data.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)));
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.errorMessage || 'Unable to fetch GST codes',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Something went wrong while fetching GST codes',
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchList();
	}, []);

	const addRow = () => {
		// Avoid stacking multiple unsaved new rows.
		if (rows.some((r) => r.isNew)) return;
		setRows((prev) => [emptyRow(), ...prev]);
	};

	const handleInput = (index, field, value) => {
		setRows((prev) =>
			prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
		);
	};

	const enableEdit = (index) => {
		setRows((prev) =>
			prev.map((r, i) => (i === index ? { ...r, isEditing: true } : r))
		);
	};

	const cancelEdit = (index) => {
		const row = rows[index];
		if (row?.isNew) {
			setRows((prev) => prev.filter((_, i) => i !== index));
		} else {
			// Discard edits by reloading from the server copy.
			fetchList();
		}
	};

	const saveRow = async (index) => {
		const row = rows[index];
		const code = String(row?.gst_code ?? '').trim();
		const tax = row?.taxPercentage;
		const description = String(row?.description ?? '').trim();

		if (!code) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please enter a GST code',
			});
			return;
		}
		if (tax === '' || tax === null || tax === undefined || isNaN(Number(tax)) || Number(tax) < 0) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please enter a valid tax percentage',
			});
			return;
		}
		if (description.length > DESCRIPTION_MAX_LENGTH) {
			notificationServices.generateNotification({
				type: 'error',
				message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`,
			});
			return;
		}

		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: 'Saving GST code...',
		});
		try {
			// igst carries the full tax percentage (stored as taxPercentage).
			const response = await gstCodeServices.createGstCode({
				gstCode: code,
				igst: Number(tax),
				description: description || null,
			});
			if (isSuccess(response)) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message || 'GST code saved successfully',
				});
				fetchList();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.errorMessage || 'Failed to save GST code',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Failed to save GST code',
			});
		} finally {
			notificationServices.closeNotification(loadingId);
		}
	};

	const deleteRow = async (row) => {
		// eslint-disable-next-line no-alert
		if (!window.confirm(`Delete GST code "${row?.gst_code}"? This cannot be undone.`)) {
			return;
		}
		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: 'Deleting GST code...',
		});
		try {
			const response = await gstCodeServices.deleteGstCode({ id: row?.id });
			if (isSuccess(response)) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message || 'GST code deleted successfully',
				});
				fetchList();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.errorMessage || 'Failed to delete GST code',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Failed to delete GST code',
			});
		} finally {
			notificationServices.closeNotification(loadingId);
		}
	};

	const headerCell = { fontWeight: 'bold !important', fontFamily: 'inherit', textAlign: 'left', backgroundColor: '#fff' };

	return (
		<Box sx={{ p: 2 }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
				<Typography variant='h6'>GST Codes</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Tooltip title='Refresh' placement='top' arrow>
						<IconButton onClick={fetchList} size='small' disabled={loading}>
							<RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
						</IconButton>
					</Tooltip>
					{canEdit && (
						<Button
							onClick={addRow}
							sx={{
								backgroundColor: '#E2E8F0',
								'&:hover': { backgroundColor: '#CBD5E1' },
								color: 'black',
							}}
						>
							+ Add GST Code
						</Button>
					)}
				</Box>
			</Box>

			<TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}>
				<Table stickyHeader size='small'>
					<TableHead>
						<TableRow>
							<TableCell sx={headerCell}>GST Code <span style={{ color: 'red' }}>*</span></TableCell>
							<TableCell sx={headerCell}>Tax % <span style={{ color: 'red' }}>*</span></TableCell>
							<TableCell sx={headerCell}>IGST %</TableCell>
							<TableCell sx={headerCell}>CGST %</TableCell>
							<TableCell sx={headerCell}>SGST %</TableCell>
							<TableCell sx={headerCell}>Description</TableCell>
							{canEdit && <TableCell sx={headerCell}>Actions</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={canEdit ? 7 : 6} align='center' sx={{ py: 4, color: '#888' }}>
									No GST codes found
								</TableCell>
							</TableRow>
						)}
						{rows.map((row, index) => {
							const taxNum = Number(row?.taxPercentage);
							const hasTax = row?.taxPercentage !== '' && !isNaN(taxNum);
							const igst = hasTax ? round2(taxNum) : '-';
							const halfTax = hasTax ? round2(taxNum / 2) : '-';
							return (
								<TableRow key={row.id ?? `new-${index}`} hover>
									<TableCell>
										{row.isEditing && row.isNew ? (
											<TextField
												size='small'
												autoFocus
												value={row.gst_code}
												onChange={(e) => handleInput(index, 'gst_code', e.target.value)}
												placeholder='e.g. GST18'
											/>
										) : (
											row.gst_code
										)}
									</TableCell>
									<TableCell>
										{row.isEditing ? (
											<TextField
												size='small'
												type='number'
												value={row.taxPercentage}
												onChange={(e) => handleInput(index, 'taxPercentage', e.target.value)}
												inputProps={{ min: 0, step: 'any' }}
												sx={{ width: 110 }}
											/>
										) : (
											row.taxPercentage
										)}
									</TableCell>
									<TableCell>{igst}</TableCell>
									<TableCell>{halfTax}</TableCell>
									<TableCell>{halfTax}</TableCell>
									<TableCell>
										{row.isEditing ? (
											<TextField
												size='small'
												value={row.description ?? ''}
												onChange={(e) => handleInput(index, 'description', e.target.value)}
												inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
												fullWidth
												placeholder='Optional description'
												sx={{ minWidth: 200 }}
											/>
										) : (
											row.description || '-'
										)}
									</TableCell>
									{canEdit && (
										<TableCell>
											<Box sx={{ display: 'flex', gap: 0.5 }}>
												{row.isEditing ? (
													<>
														<Tooltip title='Save' placement='top' arrow>
															<IconButton size='small' onClick={() => saveRow(index)} sx={{ color: '#16a34a' }}>
																<Check size={16} />
															</IconButton>
														</Tooltip>
														<Tooltip title='Cancel' placement='top' arrow>
															<IconButton size='small' onClick={() => cancelEdit(index)} sx={{ color: '#6b7280' }}>
																<X size={16} />
															</IconButton>
														</Tooltip>
													</>
												) : (
													<>
														<Tooltip title='Edit' placement='top' arrow>
															<IconButton size='small' onClick={() => enableEdit(index)} sx={{ color: '#2563eb' }}>
																<Pencil size={16} />
															</IconButton>
														</Tooltip>
														<Tooltip title='Delete' placement='top' arrow>
															<IconButton size='small' onClick={() => deleteRow(row)} sx={{ color: '#dc2626' }}>
																<Trash2 size={16} />
															</IconButton>
														</Tooltip>
													</>
												)}
											</Box>
										</TableCell>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

export default GstCodes;
